import { useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Layout } from "../components/Layout";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SeverityBadge } from "../components/badges";
import { BulkReviewBar } from "../components/BulkReviewBar";
import { useBulkReview } from "../hooks/useBulkReview";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { num, sevRail } from "../lib/format";
import { ListChecks, ChevronRight, CheckCircle2 } from "lucide-react";

export default function Worklist() {
  const qc = useQueryClient();
  const [checkFilter, setCheckFilter] = useState(null);
  const PAGE = 100;

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["worklist", checkFilter],
    queryFn: async ({ pageParam = 0 }) =>
      (await api.get(`/worklist?limit=${PAGE}&offset=${pageParam}${checkFilter ? `&check=${encodeURIComponent(checkFilter)}` : ""}`)).data,
    getNextPageParam: (lastPage, pages) => (lastPage.has_more ? pages.length * PAGE : undefined),
    keepPreviousData: true,
  });

  const pages = data?.pages || [];
  const items = pages.flatMap((p) => p.items || []);
  const byCheck = pages[0]?.by_check || [];
  const totalOpen = pages[0]?.total_open || 0;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["worklist"] });
    qc.invalidateQueries({ queryKey: ["worklist-count"] });
    qc.invalidateQueries({ predicate: (q) => ["run", "client", "client-head", "finding", "run-errors"].includes(q.queryKey?.[0]) });
  };

  const { selected, toggle, clear, selectAll, note, setNote, bulk } = useBulkReview({ onRefresh: refresh });

  const allVisibleSelected = items.length > 0 && items.every((f) => selected.has(f.check_result_GUID));
  const toggleAll = () => (allVisibleSelected ? clear() : selectAll(items.map((f) => f.check_result_GUID)));

  return (
    <Layout>
      <Breadcrumbs items={[{ label: "Portfolio", to: "/" }, { label: "Needs Review" }]} />
      <div className="mt-4 flex items-center gap-3 border-b border-border pb-5">
        <div className="grid h-11 w-11 place-items-center rounded bg-secondary">
          <ListChecks size={22} />
        </div>
        <div>
          <h1 className="font-heading text-4xl font-black">Needs Review</h1>
          <p className="mt-1 font-mono text-sm uppercase tracking-wide text-muted-foreground">
            Open findings across your customers · biggest variances first
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="mt-6 space-y-2">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={`wl-skeleton-${i}`} className="h-16 rounded-md" />
          ))}
        </div>
      ) : error ? (
        <div className="mt-6 rounded-md border border-neg/30 bg-neg/5 p-6 text-neg">Failed to load the worklist.</div>
      ) : items.length === 0 ? (
        <div className="mt-16 flex flex-col items-center gap-3 text-center" data-testid="worklist-empty">
          <CheckCircle2 size={44} className="text-pos" />
          <p className="text-lg font-semibold">All caught up.</p>
          <p className="text-sm text-muted-foreground">No open findings need your review right now.</p>
        </div>
      ) : (
        <>
          <div className="mt-5 mb-3 flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="worklist-select-all"
                checked={allVisibleSelected}
                onChange={toggleAll}
                className="h-4 w-4 accent-foreground cursor-pointer"
              />
              <span data-testid="worklist-count">
                <span className="font-bold text-foreground">{num(totalOpen)}</span> open finding
                {totalOpen === 1 ? "" : "s"}
                {totalOpen > items.length ? ` · showing ${items.length}` : ""}
              </span>
            </label>
            {checkFilter ? (
              <button
                data-testid="worklist-clear-filter"
                onClick={() => { setCheckFilter(null); clear(); }}
                className="text-xs font-semibold text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                Clear check filter
              </button>
            ) : null}
          </div>

          {byCheck.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2" data-testid="worklist-rollup">
              {byCheck.map((b) => (
                <button
                  key={b.check_code}
                  data-testid={`rollup-${b.check_code}`}
                  onClick={() => { setCheckFilter((v) => (v === b.check_code ? null : b.check_code)); clear(); }}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                    checkFilter === b.check_code ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {b.check_name} <span className="font-mono opacity-80">{num(b.n)}</span>
                </button>
              ))}
            </div>
          )}
          {byCheck.length > 0 && (
            <p className="mb-3 -mt-1 text-[11px] text-muted-foreground" data-testid="rollup-hint">
              Counts include acknowledged findings still open for review.
            </p>
          )}

          <div className="space-y-2">
            {items.map((f) => {
              const isSel = selected.has(f.check_result_GUID);
              return (
                <div
                  key={f.check_result_GUID}
                  data-testid="worklist-item"
                  className={`flex items-center gap-3 rounded-md border border-l-2 ${sevRail(f.severity)} ${
                    isSel ? "border-foreground/40 bg-secondary" : "border-border bg-card"
                  } px-3 py-3 transition-colors`}
                >
                  <input
                    type="checkbox"
                    data-testid={`worklist-check-${f.check_result_GUID}`}
                    checked={isSel}
                    onChange={() => toggle(f.check_result_GUID)}
                    className="h-4 w-4 shrink-0 accent-foreground cursor-pointer"
                    aria-label="Select finding"
                  />
                  <Link
                    to={`/findings/${f.check_result_GUID}`}
                    className="flex min-w-0 flex-1 items-center gap-3 press"
                  >
                    <SeverityBadge severity={f.severity} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{f.check_name}</span>
                        {f.employee_name ? (
                          <span className="text-sm text-muted-foreground">· {f.employee_name}</span>
                        ) : null}
                      </div>
                      <div className="text-sm text-muted-foreground truncate">{f.result_message}</div>
                    </div>
                    <div className="hidden sm:block text-right">
                      <div className="text-sm font-medium">{f.client_name}</div>
                      <div className="text-xs font-mono text-muted-foreground">Register {f.run_label}</div>
                    </div>
                    <ChevronRight size={16} className="text-muted-foreground shrink-0" />
                  </Link>
                </div>
              );
            })}
          </div>
          {hasNextPage && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                size="sm"
                data-testid="worklist-load-more"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Sticky bulk action bar */}
      <BulkReviewBar
        count={selected.size}
        note={note}
        setNote={setNote}
        onAction={(s) => bulk.mutate(s)}
        onClear={clear}
        pending={bulk.isPending}
      />
    </Layout>
  );
}
